import React, { useState, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { useTheme } from '@mui/system';
import { Box, Button, Grid, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { gridSpacing } from 'utils/constants';
import MainCard from 'components/Cards/MainCard';
import { Tree, MultiBackend, getBackendOptions } from '@minoru/react-dnd-treeview';
import AddDialog from 'components/Management/Groups/AddDialog';
import CustomNode from 'components/Management/Groups/CustomNode';
import ApiHelper from 'utils/apiHelper';
import { toast } from 'react-toastify';

const getLastId = (treeData) => {
    const reversedArray = [...treeData].sort((a, b) => {
        if (a.id < b.id) {
            return 1;
        } else if (a.id > b.id) {
            return -1;
        }
        return 0;
    });

    if (reversedArray.length > 0) {
        return reversedArray[0].id;
    }
    return 0;
};

const ManagementGroups = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [groupsData, setGroupsData] = useState([]);
    const handleDrop = (newGroup) => setGroupsData(newGroup);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [organizationName, setOrganizationName] = useState('');

    useEffect(() => {
        getGroups();
    }, []);

    const getGroups = () => {
        setLoading(true);
        ApiHelper.get('/api/management/groups/')
            .then((res) => {
                setGroupsData(res.data.groups_results);
                setOrganizationName(res.data.organization_data.name);
                setLoading(false);
            })
            .catch(() => {
                toast.error('Failed to get the groups data');
                setLoading(false);
            });
    };

    const handleDelete = (id) => {
        ApiHelper.delete(`/api/management/groups/${id}/`)
            .then(() => {
                toast.success('Group was successfully deleted.');
                getGroups();
            })
            .catch((err) => {
                toast.error(`Fail! ${err.response.data.message}`);
            });
    };

    const handleEditGroup = (id, data) => {
        ApiHelper.put(`/api/management/groups/${id}/`, {
            group_name: data.groupName,
            description: data.description,
            // is_parent: data.isParent,
            parent_id: data.isParent ? 0 : data.parentId
        })
            .then((res) => {
                if (res.status === 204) {
                    toast.error('Failed! The group name already exists in this parent group');
                } else if (res.status === 205) {
                    toast.error('Failed! The group name is the same as the parent group name');
                } else {
                    toast.success('Group was successfully updated.');
                    getGroups();
                }
            })
            .catch((err) => {
                toast.error(`Failed! ${err.response.data.message}`);
            });
    };

    const handleSubmit = (data) => {
        ApiHelper.post('/api/management/groups/', {
            group_name: data.groupName,
            description: data.description,
            // is_parent: data.isParent,
            parent_id: data.isParent ? 0 : data.parentId
        })
            .then((res) => {
                if (res.status === 204) {
                    toast.error('Failed! The group name already exists in this parent group');
                } else if (res.status === 205) {
                    toast.error('Failed! The group name is the same as the parent group name');
                } else {
                    toast.success('New group was successfully added.');
                    setShowAddDialog(false);
                    getGroups();
                }
            })
            .catch((err) => {
                toast.error(`Failed! ${err.response.data.message}`);
            });
    };

    if (!groupsData) {
        return null;
    } else {
        return (
            <MainCard title="Group Management" cardHeaderShow={false}>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12}>
                        <Grid
                            container
                            item
                            sx={{
                                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary[200]})`,
                                padding: 1
                            }}
                        >
                            <Grid item sm={9} md={9}>
                                <Typography sx={{ color: '#FFFFFF', fontSize: '16px', marginTop: 1 }}>Groups</Typography>
                            </Grid>
                            <Grid item sm={3} md={3}>
                                <Button
                                    sx={{ float: 'right', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                    onClick={() => setShowAddDialog(true)}
                                    startIcon={<AddIcon />}
                                >
                                    Add Group
                                </Button>
                                {showAddDialog && (
                                    <AddDialog tree={groupsData} onClose={() => setShowAddDialog(false)} onSubmit={handleSubmit} />
                                )}
                            </Grid>
                        </Grid>
                        <Grid container spacing={gridSpacing}>
                            <Grid item xs={12} lg={12} md={12} sm={24}>
                                {loading === true ? (
                                    <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                        Please wait...
                                    </Typography>
                                ) : groupsData.length === 0 ? (
                                    <Typography sx={{ mt: 3, textAlign: 'center' }}>No Groups data</Typography>
                                ) : (
                                    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
                                        <Box sx={{ mt: 2 }}>
                                            <Tree
                                                tree={groupsData}
                                                rootId={0}
                                                render={(node, options) => (
                                                    <CustomNode
                                                        data={node}
                                                        options={options}
                                                        organizationName={organizationName}
                                                        handleEditGroup={handleEditGroup}
                                                        onDelete={handleDelete}
                                                    />
                                                )}
                                                // dragPreviewRender={(monitorProps) => <div>{monitorProps.item}</div>}
                                                onDrop={handleDrop}
                                                initialOpen={true}
                                            />
                                        </Box>
                                    </DndProvider>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </MainCard>
        );
    }
};

export default ManagementGroups;
